import React, { useState } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Spinner
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import { resetPassword } from '../../../../redux/slices/authSlice';
import './resetPassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {loading} = useSelector((state) => state.authSlice);
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    const passwordData = {
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword
    };

    dispatch(resetPassword(passwordData, navigate));
  };

  return (
    <div className="reset-password-container">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="reset-card">
              <Card.Body className="p-4">
                <div className="text-center">
                  <div className="icon-container mx-auto">
                    <FeatherIcon icon="key" size={24} />
                  </div>
                  <h2 className="form-title">Reset Password</h2>
                  <p className="form-subtitle">
                    Please enter your current password and choose a new one
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-4">
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="alert-circle" size={18} className="me-2 alert-icon" /> {error}
                    </div>
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="password-input-group">
                    <Form.Label className="form-label">Current Password</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FeatherIcon icon="lock" size={18} className="input-icon" />
                      </span>
                      <Form.Control
                        type={showPasswords.current ? "text" : "password"}
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleChange}
                        required
                        placeholder="Enter your current password"
                      />
                      <span className="input-group-text password-toggle-container">
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => togglePasswordVisibility('current')}
                          tabIndex="-1"
                        >
                          <FeatherIcon 
                            icon={showPasswords.current ? "eye-off" : "eye"} 
                            size={18} 
                          />
                        </button>
                      </span>
                    </div>
                  </Form.Group>

                  <Form.Group className="password-input-group">
                    <Form.Label className="form-label">New Password</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FeatherIcon icon="key" size={18} className="input-icon" />
                      </span>
                      <Form.Control
                        type={showPasswords.new ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        required
                        placeholder="Enter your new password"
                      />
                      <span className="input-group-text password-toggle-container">
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => togglePasswordVisibility('new')}
                          tabIndex="-1"
                        >
                          <FeatherIcon 
                            icon={showPasswords.new ? "eye-off" : "eye"} 
                            size={18} 
                          />
                        </button>
                      </span>
                    </div>
                  </Form.Group>

                  <Form.Group className="password-input-group">
                    <Form.Label className="form-label">Confirm New Password</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FeatherIcon icon="check" size={18} className="input-icon" />
                      </span>
                      <Form.Control
                        type={showPasswords.confirm ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        placeholder="Confirm your new password"
                      />
                      <span className="input-group-text password-toggle-container">
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => togglePasswordVisibility('confirm')}
                          tabIndex="-1"
                        >
                          <FeatherIcon 
                            icon={showPasswords.confirm ? "eye-off" : "eye"} 
                            size={18} 
                          />
                        </button>
                      </span>
                    </div>
                  </Form.Group>

                  <div className="d-grid mt-4">
                    <Button 
                      className="submit-btn" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Updating Password...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPassword;
